import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'qs';
import _ from 'lodash';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar/index';
import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { Table, Modal, Tooltip, Button } from 'antd';
import { checkPermission } from '@/utils/permission';
import { cleanObject, isValidDate, laboCaseStatuses, renderLaboCaseStatusTag, stripHtml } from '@/utils/helpers';
import HtmlParser from 'react-html-parser';
import TextTruncate from 'react-text-truncate';
import { fetchLaboCaseList, fetchLaboCaseDetail, deleteLaboCases } from '@/redux/actions/laboCaseActions';
import LaboCaseDetail from '@/routes/labo-cases/LaboCaseDetail';
import LaboCaseForm from '@/routes/labo-cases/LaboCaseForm';
import PopupWrapper from '@/components/PopupWrapper';
import BaseSelect from '@/components/Elements/BaseSelect';
import { fetchActiveLabos } from '@/redux/actions/laboActions';
import moment from 'moment';
import BaseRangeFilter from '@/components/BaseRangeFilter';
import CustomerInfoColumn from '@/components/customers/CustomerInfoColumn';
import CreatedAtCell from '@/components/CreatedAtCell';

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/labo-cases', breadcrumbName: 'Ca gửi labo' },
];

export default function LaboCaseList() {
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();

    const items = useSelector(state => state.laboCaseReducer.list.items);
    const pagination = useSelector(state => state.laboCaseReducer.list.pagination);
    const laboList = useSelector((state) => state.laboReducer.activeList.items);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [currentItem, setCurrentItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const routeName = 'labo_cases';
    const allowCreate = checkPermission(routeName, 'create');
    const allowDelete = checkPermission(routeName, 'remove');
    const alloDetail = checkPermission(routeName, 'detail');

    const query = qs.parse(location.search.slice(1));

    useEffect(() => {
        dispatch(fetchActiveLabos());
    }, [dispatch]);

    const loadData = useCallback((q = query) => {
        setIsLoading(true);
        dispatch(fetchLaboCaseList(q)).finally(() => setIsLoading(false));
    }, [dispatch, query]);

    useEffect(() => {
        loadData();
    }, [location.search]);

    const handleChangeTable = (pagination, sorter) => {
        let q = { ...query };

        if (!_.isEmpty(pagination)) {
            q.page = pagination.current;
            q.per_page = pagination.pageSize;
        }

        if (!_.isEmpty(sorter)) {
            if (sorter.order && sorter.field) {
                q.sort_field = sorter.field;
                q.sort_type = sorter.order === 'ascend' ? 'ASC' : 'DESC';
            } else {
                delete q.sort_field;
                delete q.sort_type;
            }
        }

        history.push({
            pathname: location.pathname,
            search: qs.stringify(q),
        });
    };

    const handleDetail = async (id) => {
        setCurrentId(id);
        setDetailLoading(true);
        const res = await dispatch(fetchLaboCaseDetail(id));
        setDetailLoading(false);
        if (!res) return;
        openModal('detail', res);
    };

    const openModal = (mode = 'create', item = null) => {
        setModalMode(mode);
        setIsOpenModal(true);
        setCurrentItem(item);
    };

    const handleRemove = () => {
        dispatch(deleteLaboCases(selectedRowKeys)).then(() => {
            setSelectedRowKeys([]);
            loadData();
            setTimeout(() => Modal.destroyAll(), 800);
        });
    };

    const formatDate = (d) => (moment.isMoment(d) && d.isValid()) ? d.format('YYYY-MM-DD') : undefined;

    const handleFilterChange = (name, value) => {
        let q = { ...query, [name]: value };

        const rangeFilters = {
            send_date: ['send_date_from', 'send_date_to'],
            created_at: ['created_at_from', 'created_at_to'],
        };

        if (rangeFilters[name]) {
            const [fromKey, toKey] = rangeFilters[name];
            const [start, end] = value || [];

            q[fromKey] = formatDate(start);
            q[toKey] = formatDate(end);

            // Nếu không có ngày hợp lệ, xóa cả hai
            if (!q[fromKey] && !q[toKey]) {
                delete q[fromKey];
                delete q[toKey];
            }

            delete q[name];
        }
        console.log(name, value);
        q = cleanObject(q);
        history.push({ pathname: location.pathname, search: qs.stringify(q) });
    };

    const columns = [
        {
            title: 'ID', dataIndex: 'id', key: 'id', sorter: true, width: '50px'
        },
        {
            title: '#', align: 'center', width: '50px',
            render: (_, record) => (
                <Button size="small" type="dashed" className='btn-edit' icon={<EyeOutlined />} onClick={() => handleDetail(record.id)} disabled={!alloDetail}>Xem</Button>
            )
        },
        {
            title: 'Khách hàng',
            key: 'customer',
            width: 300,
            render: (_, record) => {
                const customer = {
                    id: record?.customer?.id,
                    code: record?.customer?.code,
                    full_name: record?.customer?.full_name,
                    branch_name: record?.customer?.branch?.name,
                    conditions: record?.customer?.condition_names_string,
                    customer_source: record?.customer?.customer_source?.name,
                    customer_status: record?.customer?.customer_status?.name,
                    customer_status_color: record?.customer?.customer_status?.text_color,
                    medical_book_number: record?.customer?.medical_book_number
                }
                return (
                    <CustomerInfoColumn
                        record={customer}
                        nameField="full_name"
                        idField="id"
                        codeField="code"
                        branchField="branch_name"
                        enableLink={true}
                    />
                )
            }
        },
        { title: 'Labo', dataIndex: 'labo', key: 'labo', render: (_, r) => r.labo?.name },
        { title: 'Ngày gửi', dataIndex: 'send_date', width: '100px', render: t => isValidDate(t, false) },
        { title: 'Ngày nhận', dataIndex: 'receive_date', width: '100px', render: t => isValidDate(t, false) },
        { title: 'Ngày trả', dataIndex: 'return_date', width: '100px', render: t => isValidDate(t, false) },
        { title: 'Trạng thái', dataIndex: 'status', render: (_, r) => renderLaboCaseStatusTag(r.status) },
        {
            title: 'Ghi chú', dataIndex: 'note', key: 'note', width: '200px',
            render: (text) => (
                <div style={{ wordBreak: "break-word" }}>
                    <Tooltip title={HtmlParser(text)} placement="topLeft">
                        <TextTruncate line={1} truncateText="…" text={stripHtml(text)} />
                    </Tooltip>
                </div>
            )
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'created_at',
            key: 'created_at',
            width: '220px',
            render: (text, record) => (
                <CreatedAtCell
                    createdAt={text}
                    user={record?.created_user}
                />
            )
        }
    ];

    return (
        <div>
            <PageTitle routes={routes} title="Ca gửi labo" />
            <TableActionBar
                isShowAddButton={allowCreate}
                isShowDeleteButton={allowDelete}
                onAdd={() => openModal('create')}
                disabled={!selectedRowKeys.length}
                onDelete={handleRemove}
                showSearch
                showFilter={true}
                activeFilter={query.status || query.labo_id || query.keyword || query.created_at_from || query.created_at_to || query.send_date_from || query.send_date_to}
                onSearch={value => handleFilterChange('keyword', value)}
                defaultKeyword={query.keyword}
                filters={[
                    <BaseSelect
                        options={laboList || []}
                        optionValue="id"
                        optionLabel="name"
                        showSearch
                        placeholder="Chọn labo"
                        className="w-100"
                        onChange={(value) => handleFilterChange("labo_id", value)}
                        defaultValue={parseInt(query.labo_id) || null}
                    />,
                    <BaseSelect
                        options={laboCaseStatuses || []}
                        showSearch
                        placeholder="Trạng thái"
                        className="w-100"
                        onChange={(value) => handleFilterChange("status", value)}
                        defaultValue={query.status || null}
                    />,
                    <BaseRangeFilter
                        name="created_at"
                        query={query}
                        onChangeFilter={handleFilterChange}
                        placeholder={['Ngày tạo', 'Đến ngày']}
                    />,
                    <BaseRangeFilter
                        name="send_date"
                        query={query}
                        onChangeFilter={handleFilterChange}
                        placeholder={['Ngày gửi', 'Đến ngày']}
                    />
                ]}
            >
                <Button icon={<ReloadOutlined />} loading={isLoading} onClick={() => loadData()}>Refresh</Button>
                {selectedRowKeys.length > 0 && <span>Đang chọn {selectedRowKeys.length} bản ghi</span>}
            </TableActionBar>

            <Table
                rowKey='id'
                size='small'
                tableLayout='auto'
                columns={columns}
                rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                loading={isLoading}
                dataSource={items || []}
                pagination={{
                    pageSize: pagination.perPage,
                    current: pagination.currentPage,
                    total: pagination.total,
                    showTotal: total => `Tổng ${total} bản ghi`,
                    defaultPageSize: 30, showSizeChanger: true, pageSizeOptions: ['10', '20', '30', '40', '50', '100', '200']
                }}
                onChange={(pagination, filter, sorter) => handleChangeTable(pagination, sorter)}
                scroll={{ x: 'max-content' }}
            />

            <PopupWrapper
                mode={modalMode}
                visible={isOpenModal}
                currentItem={currentItem}
                onCancel={() => setIsOpenModal(false)}
                loadData={() => {
                    loadData();
                    handleDetail(currentId);
                }}
                detailLoading={detailLoading}
                FormComponent={LaboCaseForm}
                DetailComponent={LaboCaseDetail}
            />
        </div>
    );
}
